import { DatePicker as AntDatePicker } from "antd";
import {
  DatePickerProps as AntDatePickerProps,
  RangePickerProps as AntRangePickerProps,
  RangePickerValue as AntRangePickerValue,
} from "antd/lib/date-picker/interface";
import * as React from "react";
import styled, { createGlobalStyle } from "styled-components";

import { FormItemDescription } from "./Form";
import { theme } from "./theme";

const moment = require("moment");
const { RangePicker } = AntDatePicker;

type DatePickerProps = Omit<AntDatePickerProps, "value" | "onChange"> & {
  value?: string;
  field: string;
  fieldDescription?: React.ReactNode;
  hideYearIcon?: boolean;
  hideMonthIcon?: boolean;
  onChange?: (value: string) => void;
};

type RangePickerValue =
  | undefined[]
  | [string]
  | [undefined, string]
  | [string, string];
type RangePickerProps = Omit<AntRangePickerProps, "value" | "onChange"> & {
  value?: RangePickerValue;
  field: string;
  onChange?: (value: [string, string]) => void;
};

class DatePicker extends React.PureComponent<DatePickerProps> {
  render() {
    const { field, value, fieldDescription, format } = this.props;
    return (
      <>
        <GlobalStyle />
        {fieldDescription && (
          <FormItemDescription>{fieldDescription}</FormItemDescription>
        )}
        <DatePickerCustom
          {...this.props}
          value={!value ? undefined : moment(value, format)}
          data-test={field}
          className={this.getClassName()}
          dropdownClassName={this.getDropdownClassName()}
          onChange={(_: moment.Moment | null, dateString: string) =>
            this.handleDateChange(dateString)
          }
        />
      </>
    );
  }

  private getClassName = () => {
    const { className, hideMonthIcon, hideYearIcon } = this.props;
    const extraClasses = [];
    if (hideMonthIcon) {
      extraClasses.push("is-hide-month");
    }
    if (hideYearIcon) {
      extraClasses.push("is-hide-year");
    }
    return `${className || ""} ${extraClasses.join(" ")}`;
  };

  private getDropdownClassName = () => {
    const { dropdownClassName, hideMonthIcon, hideYearIcon } = this.props;
    const extraClasses = [];
    if (hideMonthIcon) {
      extraClasses.push("is-hide-month");
    }
    if (hideYearIcon) {
      extraClasses.push("is-hide-year");
    }
    return `${dropdownClassName || ""} ${extraClasses.join(" ")}`;
  };

  private handleDateChange = (dateString: string) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(dateString);
    }
  };
}

class DateRangePicker extends React.PureComponent<RangePickerProps> {
  render() {
    const { value, field } = this.props;
    const momentValue: any = [];
    if (value && value.length > 0) {
      momentValue.push(value[0] ? moment(value[0]) : undefined);
    }
    if (value && value.length > 1 && value[1]) {
      momentValue.push(moment(value[1]));
    }
    return (
      <RangePicker
        {...this.props}
        value={momentValue}
        date-test={field}
        onChange={this.handleDateRangeChange}
      />
    );
  }

  private handleDateRangeChange = (
    _: AntRangePickerValue,
    dateString: [string, string]
  ) => {
    const { onChange } = this.props;
    if (onChange) {
      onChange(dateString);
    }
  };
}

export {
  DatePicker,
  DateRangePicker,
  DatePickerProps,
  RangePickerProps,
  RangePickerValue,
};

export const DatePickerCustom = styled(AntDatePicker)`
  .ant-input {
    border-color: ${(props) => props.theme.gray};
    border-radius: 2px;
  }

  .ant-calendar-picker-icon {
    cursor: pointer;
  }
`;

const GlobalStyle = createGlobalStyle`
  .ant-calendar-picker-container{
    .ant-calendar {
      width: 330px;
    }

    .ant-calendar-input-wrap {
      display: none;
    }
    .ant-calendar-range {
      width: 675px;
    }
    .ant-calendar-range .ant-calendar-in-range-cell:before {
      top: 0;
      bottom: 0;
      background-color: ${theme.dateSelectionColor};
    }
    .ant-calendar-in-range-cell .ant-calendar-date {
      color: ${theme.white};
    }
    .ant-calendar-header {
      .ant-calendar-month-select, .ant-calendar-year-select {
        font-weight: 400;
        color: ${theme.textDeepGray}
      }
    }
    .ant-calendar-table {
      border: none;
    }

    .ant-calendar-cell {
      height: 40px;
      padding: 0;
    }

    /* stylelint-disable-next-line no-duplicate-selectors */
    .ant-calendar-in-range-cell  .ant-calendar-date{
      color: ${theme.white};
      &:hover{
        background-color: unset;
      }
    }

    /* stylelint-disable-next-line no-duplicate-selectors */
    .ant-calendar-cell {
      border: 1px solid ${theme.dateBorderColor};
       /* stylelint-disable-next-line no-descending-specificity */
      .ant-calendar-date {
        width: 43px;
        height: 39px;
        line-height: 37px;
        border-radius: unset;
      }
    }

    .ant-calendar-disabled-cell {
      /* stylelint-disable-next-line no-descending-specificity */
      .ant-calendar-date, .ant-calendar-date:hover{
        background: transparent;
      }
      &.ant-calendar-selected-day .ant-calendar-date:before,
      &.ant-calendar-today .ant-calendar-date:before{
        width: calc(100% + 2px);
        height: calc(100% + 2px);
        left: -1px;
        border-radius: 0;
      }
    }

    &.is-hide-year {
      .ant-calendar-prev-year-btn, .ant-calendar-next-year-btn {
        display: none;
      }

      .ant-calendar-prev-month-btn {
        left: 7px;
      }

      .ant-calendar-next-month-btn {
        right: 7px;
      }
    }
  }
  .ant-calendar-picker-container-placement-bottomLeft{
    .ant-calendar {
      margin-top: 60px;
    }
    &::before {
      content: ' ';
      position: absolute;
      left: 50px;
      top: 54px;
      width: 13px;
      height: 13px;
      overflow: hidden;
      pointer-events: none;
      transform: rotate(45deg);
      background: ${theme.white};
      box-shadow: -2px -2px 5px rgba(0,0,0,0.06);
      z-index: 1;
    }
  }

  .ant-calendar-picker-container-placement-topLeft {
    &&{padding-bottom: 60px;}
    &::after {
      content: ' ';
      position: absolute;
      left: 50px;
      bottom: 53px;
      width: 13px;
      height: 13px;
      overflow: hidden;
      pointer-events: none;
      transform: rotate(45deg);
      background: ${theme.white};
      box-shadow: 2px 5px 5px rgba(0,0,0,0.06);
      z-index: 1;
    }
  }
`;
